/**
 * Publishes the visual viewport as CSS custom properties — see index.css
 * §THE VISUAL VIEWPORT for what reads them and why.
 *
 * Three properties on <html>, written only when a value actually changes:
 *
 *   --vv-top     visualViewport.offsetTop
 *   --vv-bottom  innerHeight - (offsetTop + height)   — the keyboard, when up
 *   --vv-h       visualViewport.height
 *
 * The `scroll` event fires on ordinary page scrolls too, so the write is
 * gated on a change: on a plain scroll all three values are unchanged and
 * nothing touches the style, which matters because a custom property on the
 * root invalidates style for the whole tree.
 *
 * Idempotent, so both entry points (the app and the layout harness) can call
 * it without coordinating. A browser without the API leaves the properties
 * undefined and every consumer's `var(--vv-*, fallback)` applies.
 */
let installed = false;

export function installVisualViewportVars(): void {
  if (installed || typeof window === "undefined") return;
  const vv = window.visualViewport;
  if (!vv) return;
  installed = true;

  const root = document.documentElement;
  let last = "";
  const update = () => {
    const top = Math.max(0, Math.round(vv.offsetTop));
    const bottom = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
    const h = Math.round(vv.height);
    const key = `${top}|${bottom}|${h}`;
    if (key === last) return;
    last = key;
    root.style.setProperty("--vv-top", `${top}px`);
    root.style.setProperty("--vv-bottom", `${bottom}px`);
    root.style.setProperty("--vv-h", `${h}px`);
  };

  vv.addEventListener("resize", update);
  vv.addEventListener("scroll", update);
  window.addEventListener("orientationchange", update);
  update();
}
